import React, { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react';
import { commAlertOverlayClass, commAlertPanelClass } from './communicationsUi';

export type CommunicationsAlertTone = 'success' | 'error' | 'warning';

export interface CommunicationsAlertDialogProps {
  open: boolean;
  tone: CommunicationsAlertTone;
  title: string;
  message: string;
  onClose: () => void;
}

const toneConfig: Record<
  CommunicationsAlertTone,
  { icon: React.ReactNode; iconBg: string; btn: string }
> = {
  success: {
    icon: <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    btn: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
  },
  error: {
    icon: <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />,
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    btn: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    icon: <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    btn: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
  },
};

const CommunicationsAlertDialog: React.FC<CommunicationsAlertDialogProps> = ({
  open,
  tone,
  title,
  message,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const config = toneConfig[tone];

  return (
    <div
      className={commAlertOverlayClass}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="communications-alert-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={commAlertPanelClass}>
        <div className="flex items-start gap-3 p-5">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${config.iconBg}`}
          >
            {config.icon}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              id="communications-alert-title"
              className="text-base font-semibold text-gray-900 dark:text-white"
            >
              {title}
            </h3>
            <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {message}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="border-t border-gray-100 px-5 py-4 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:min-w-[6rem] ${config.btn}`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunicationsAlertDialog;
